import { Link } from "@/lib/nav";

import Pill from "@buildpy/ui/pill";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { formatNumber } from "@buildpy/core/lib/format";
import type { IntakeDesigner } from "@buildpy/core/modules/customer/intake-designer";
import { PAIR_STAGE_LABEL } from "@buildpy/core/modules/portfolio/pairs";
import type { PublicPortfolioOrg } from "@buildpy/core/modules/portfolio/public-service";
import type { PublicPortfolioHero } from "@buildpy/core/modules/portfolio/public-view";
import PublicPhoto from "@/modules/portfolio/ui/public-photo";
import { hasPublicPageTrust, type PublicPageReview, type PublicPageTrust } from "@buildpy/core/modules/public-page/view";

// 공개 링크 A·B 가 같이 쓰는 칸 — 같은 데이터를 같은 모양으로 보인다.

// inquiry 가 null 이면(조직이 「상담 문의」 를 끔) 버튼 없이 제목만.
export const PublicPageHeader = ({ org, inquiry }: { org: PublicPortfolioOrg; inquiry: string | null }) => (
  <header className="border-b border-border bg-card px-4 pb-4 pt-safe-4">
    <div className="mx-auto flex w-full max-w-5xl items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="truncate text-sm text-muted-foreground">{org.name}</p>
        <h1 className="text-xl font-semibold">시공사례</h1>
      </div>
      {inquiry && (
        <Button asChild className="shrink-0" size="sm">
          <Link data-testid="public-page-inquiry" href={inquiry}>
            상담 문의
          </Link>
        </Button>
      )}
    </div>
  </header>
);

export const HeroPhotos = ({ hero, afterLabel = PAIR_STAGE_LABEL.AFTER }: { hero: PublicPortfolioHero; afterLabel?: string }) => {
  if (hero.before_src && hero.after_src) {
    return (
      <div className="grid grid-cols-2 gap-px bg-border" data-testid="public-page-hero-before-after">
        {[
          { label: PAIR_STAGE_LABEL.BEFORE, src: hero.before_src },
          { label: afterLabel, src: hero.after_src },
        ].map((photo) => (
          <figure key={photo.label} className="relative bg-card">
            <PublicPhoto alt={`${hero.title} ${photo.label}`} className="aspect-[4/3] w-full" src={photo.src} />
            <figcaption className="absolute left-2 top-2">
              <Pill>{photo.label}</Pill>
            </figcaption>
          </figure>
        ))}
      </div>
    );
  }
  return <PublicPhoto alt={hero.title} className="aspect-[16/9] w-full" src={hero.cover_src} />;
};

export const PublicPageEmpty = () => (
  <SectionCard as="div" className="p-5 text-center text-sm text-muted-foreground" padding="none" data-testid="public-portfolio-empty">
    아직 공개된 시공사례가 없습니다.
  </SectionCard>
);

const trustRowsOf = (trust: PublicPageTrust): { label: string; value: string }[] => [
  ...(trust.finished_count !== null ? [{ label: "완료 현장", value: `${formatNumber(trust.finished_count)}곳` }] : []),
  ...(trust.warranty_months !== null ? [{ label: "하자보증", value: `준공 후 ${formatNumber(trust.warranty_months)}개월` }] : []),
  ...(trust.license_no !== null ? [{ label: "건설업 등록번호", value: trust.license_no }] : []),
];

export const PublicPageTrustCard = ({ trust }: { trust: PublicPageTrust }) =>
  hasPublicPageTrust(trust) ? (
    <SectionCard data-testid="public-page-trust" title="맡기시기 전에">
      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {trustRowsOf(trust).map((row) => (
          <div key={row.label} className="flex flex-col gap-y-0.5">
            <dt className="text-xs text-muted-foreground">{row.label}</dt>
            <dd className="text-base font-semibold">{row.value}</dd>
          </div>
        ))}
      </dl>
    </SectionCard>
  ) : null;

export const PublicPageDesigners = ({ designers, title = "담당 디자이너" }: { designers: readonly IntakeDesigner[]; title?: string }) =>
  designers.length > 0 ? (
    <SectionCard data-testid="public-page-designers" title={title}>
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {designers.map((designer) => (
          <li key={designer.id} className="flex flex-col gap-y-0.5">
            <p className="text-sm font-semibold">{designer.name}</p>
            {designer.intro && <p className="text-sm text-muted-foreground">{designer.intro}</p>}
          </li>
        ))}
      </ul>
    </SectionCard>
  ) : null;

export const PublicPageReviews = ({ reviews }: { reviews: readonly PublicPageReview[] }) =>
  reviews.length > 0 ? (
    <SectionCard data-testid="public-page-reviews" title="고객 후기">
      <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {reviews.map((review) => (
          <li key={review.id} className="flex flex-col gap-y-1 rounded-md bg-muted p-3">
            <p className="whitespace-pre-line text-sm">{review.body}</p>
            <p className="text-xs text-muted-foreground">{review.author_label}</p>
          </li>
        ))}
      </ul>
    </SectionCard>
  ) : null;

export const PublicPageCta = ({
  inquiry,
  title = "우리 집도 상담받아 보세요",
  note,
}: {
  inquiry: string;
  title?: string;
  note?: string;
}) => (
  <SectionCard className="flex flex-col items-center gap-y-3 text-center" data-testid="public-page-cta" padding="lg">
    <p className="text-base font-semibold">{title}</p>
    {note && <p className="text-sm text-muted-foreground">{note}</p>}
    <Button asChild>
      <Link href={inquiry}>상담 문의</Link>
    </Button>
  </SectionCard>
);

export const PublicPageFooter = ({ org }: { org: PublicPortfolioOrg }) => (
  <footer className="border-t border-border pt-4 text-center text-xs text-muted-foreground">{org.name}</footer>
);
